import { memo, useRef, useState } from "react";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import { DisaggregatedMetric } from "../../services/types";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from "chart.js";

interface AgeGenderChartProps {
    indicators: DisaggregatedMetric[] | null;
    title: string;
    height: string;
}

const AgeGenderChart = memo(({ indicators, title, height }: AgeGenderChartProps) => {
    const [data, setData] = useState<{ label: string, female: number, male: number }[]>([]);
    const backgroundColor = '#125687';

    useDeepCompareEffect(() => {
        if (!indicators) return;

        const femaleByAge = indicators.find(data => data.id === 1147);
        const maleByAge = indicators.find(data => data.id === 1148);
        
        if (!femaleByAge || !maleByAge) return;

        const versionFemale = femaleByAge.versions[0];
        const versionMale = maleByAge.versions[0];
        if (!versionFemale || !versionMale) return;

        const tallyMap = new Map<string, { female: number; male: number }>();

        versionFemale.disaggregations.forEach((d) => {
            tallyMap.set(d.name.trim(), { female: d.value, male: 0 });
        });

        versionMale.disaggregations.forEach((d) => {
            const entry = tallyMap.get(d.name.trim());
            if (entry) {
                entry.male = d.value;
            } else {
                tallyMap.set(d.name.trim(), { female: 0, male: d.value });
            }
        });

        const allValues = Array.from(tallyMap.entries()).map(([label, values]) => ({
            label,
            female: values.female,
            male: values.male,
        }));

        setData(allValues);
    }, [indicators]);


    // Register the plugin to all charts:
    Chart.register(ChartDataLabels);
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"bar"> | null>(null);

    useDeepCompareEffect(() => {
        const ctx = chartRef.current?.getContext("2d");
        if (ctx) {
            // Destroy existing chart instance if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Extract labels and data
            const labels = data.map(d => d.label);
            const femaleData = data.map(d => d.female);
            const maleData = data.map(d => d.male);

            // Define a percentage to increase the maxValue by (e.g., 10%)
            // const paddingPercentage = 0.1;

            // Set max and min values for label padding. Ensure min and max are correctly set, avoiding negative start for x-axis
            // Calculate the maxValue with additional padding
            // const maxValue = Math.round(Math.max(...rawValues) * (1 + paddingPercentage));
            const minValue = Math.min(0, ...femaleData, ...maleData);


            chartInstance.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: "Female",
                            data: femaleData,
                            backgroundColor: "#6FAEDE",
                          },
                          {
                            label: "Male",
                            data: maleData,
                            backgroundColor: "#00A18A",
                          },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'x',
                    plugins: {
                        legend: {
                            display: true,
                            labels: {
                                font: {
                                    size: 11,
                                },
                                boxWidth: 6,
                                boxHeight: 6,
                                usePointStyle: true,
                                pointStyle: 'circle',
                            },
                            position: 'bottom',
                        },
                        datalabels: {
                            display: false,
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const value = context.formattedValue;
                                    return value;
                                }
                            },
                            bodyColor: '#FFFFFF',
                        },
                    },
                    layout: {
                        padding: {
                            left: 10,
                            right: 40,
                            top: 10,
                            bottom: 10,
                        }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            min: minValue,
                            // max: maxValue,
                            grid: {
                                display: false,
                                drawTicks: true,
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    size: 11,
                                    weight: 'bold',
                                },
                                padding: 0,
                                color: '#4D4D4D',
                                autoSkip: false,  // Ensure all labels are displayed
                            },
                        },
                        y: {
                            
                            ticks: {
                                display: true,
                                color: "#4D4D4D",
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    size: 11,
                                    weight: 'bold',
                                },
                                padding: 10,
                            },
                            grid: {
                                display: true,
                                color: '#D3D3D3',
                            },
                            border: {
                                display: false,
                                dash: [6, 4],
                            },
                        },
                    },
                },
            });
        }
    }, [data, title, backgroundColor]);

    return (
        <div className="w-full">
            <div className={height ? height : `h-80`}> {/* Adjust height as needed */}
                <canvas ref={chartRef} className="w-full bg-[#F0F0F0]"/>
            </div>
        </div>
    );
});

export default AgeGenderChart;